import { Component, EventEmitter, Output, forwardRef, ViewChild, NgZone, ChangeDetectorRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CrudTableViewConfig, CrudTableViewColumn } from '../../../utils/list-grid';
import { cloneDeep } from 'lodash';
import { HeadingType } from '../../../utils/heading';
import { InputSize } from '../../../utils/shared';
import { DateFormat } from '../../../utils/date-picker';
import { GridBuilderColumnsComponent } from '../grid-builder-columns/grid-builder-columns.component';
import { GridBuilderSettingsComponent } from '../grid-builder-settings/grid-builder-settings.component';
import { NotificationService } from '../../../services/notification/notification.service';
import { AlertTypes } from '../../../utils/alert/alert-types';
import { TextComponentConfig } from '../../../utils/text';

@Component({
  selector: 'rappider-grid-builder',
  templateUrl: './grid-builder.component.html',
  styleUrls: ['./grid-builder.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GridBuilderComponent),
      multi: true
    }
  ]
})
export class GridBuilderComponent implements ControlValueAccessor {
  @Output() blur = new EventEmitter<CrudTableViewConfig>();

  @ViewChild(GridBuilderColumnsComponent) gridBuilderColumnsComponent: GridBuilderColumnsComponent;
  @ViewChild(GridBuilderSettingsComponent) gridBuilderSettingsComponent: GridBuilderSettingsComponent;

  // Data model
  _value: CrudTableViewConfig;
  tempValue: CrudTableViewConfig;
  tempValueWithoutChanges: CrudTableViewConfig;
  jsonModalBackupData: CrudTableViewConfig;
  jsonEditorSaved = false;

  // Initial config
  initialGridConfig: CrudTableViewConfig = {
    columns: [],
    title: {},
    listActions: [],
    itemActions: []
  };
  /* holds the modal visibility state */
  isEditModalVisible = false;
  /* holds the JSON editor modal visibility state */
  isJsonEditorModalVisible = false;
  modalAutoClose = true;
  modalInitialized = false;
  populatedData: Record<string, unknown>[];
  columnToEdit: { column: CrudTableViewColumn; index: number };

  // Options
  headingTypeOptions = Object.entries(HeadingType).map(([key, value]) => ({
    key: key,
    value: value
  }));

  inputSizeOptions = Object.entries(InputSize).map(([key, value]) => ({
    key: key,
    value: value
  }));

  dateFormatOptions = Object.entries(DateFormat).map(([key, value]) => ({
    key: key,
    value: value
  }));

  /* validation properties */
  validationErrors: { id: string; message: string }[] = [];
  validationAlertVisible = false;
  validationAlertConfig = {
    type: AlertTypes.Error,
    title: {
      type: HeadingType.H5,
      content: 'Required Fields Missing'
    },
    description: <TextComponentConfig>{ text: '' },
    closeable: false,
    showIcon: true
  };

  get value() {
    return this._value;
  }

  set value(value: CrudTableViewConfig) {
    this._value = value;
    this.onChange(value);
    this.onTouched();
  }

  constructor(
    private ngZone: NgZone,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef
  ) { }

  onChange: any = () => { };
  onTouched: any = () => { };

  writeValue(value: CrudTableViewConfig) {
    if (!value) {
      if (!this.tempValue) {
        this.tempValue = cloneDeep(this.initialGridConfig);
      }
      this._value = cloneDeep(this.initialGridConfig);
    } else {
      this._value = cloneDeep(value);
      this.tempValue = cloneDeep(value);
    }
    this.tempValueWithoutChanges = cloneDeep(this.tempValue);
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  onItemsChange(newValue?: CrudTableViewConfig) {
    if (newValue) {
      this.tempValue = cloneDeep(newValue);
    } else {
      this.tempValue = { ...this.tempValue };
    }
    if (this.validationAlertVisible) {
      this.updateValidationMessages();
    }
  }

  onColumnsChange() {
    this.tempValue = { ...this.tempValue };
    if (this.validationAlertVisible) {
      this.updateValidationMessages();
    }
  }

  onGridConfigSave() {
    this.validationErrors = [];

    if (!this.validateColumns(true)) {
      this.modalAutoClose = false;

      if (this.gridBuilderSettingsComponent) {
        this.gridBuilderSettingsComponent.showValidationErrors();
      }

      this.ngZone.run(() => {
        this.modalAutoClose = true;
      });
      return;
    }

    this.validationAlertVisible = false;
    this.validationErrors = [];
    this.modalAutoClose = true;
    this.value = cloneDeep(this.tempValue);
    this.tempValueWithoutChanges = cloneDeep(this.tempValue);
    this.blur.emit(this.value);
    this.closeEditModal();
  }

  onCancel() {
    this.closeEditModal();
  }

  /**
   * Open modal to edit grid configuration
   */
  openEditModal() {
    this.validationErrors = [];
    this.validationAlertVisible = false;
    this.modalInitialized = false;

    if (this._value) {
      this.tempValue = cloneDeep(this._value);
    } else {
      this.tempValue = cloneDeep(this.initialGridConfig);
    }

    if (!this.tempValue.listActions) {
      this.tempValue.listActions = [];
    }
    if (!this.tempValue.itemActions) {
      this.tempValue.itemActions = [];
    }

    this.tempValueWithoutChanges = cloneDeep(this.tempValue);
    this.isEditModalVisible = true;
    this.cdr.detectChanges();
  }

  /**
   * Handler for modal after open event
   */
  onGridModalAfterOpen() {
    if (this.modalInitialized) {
      return;
    }

    if (this.gridBuilderSettingsComponent) {
      this.gridBuilderSettingsComponent.setActiveTab(0);

      this.ngZone.runOutsideAngular(() => {
        this.gridBuilderSettingsComponent.resetValidationErrors();
      });
    }

    this.modalInitialized = true;
    this.cdr.detectChanges();
  }

  /**
   * Reset settings tab to Column Settings (index 0)
   */
  resetSettingsTab() {
    if (this.gridBuilderSettingsComponent) {
      this.gridBuilderSettingsComponent.setActiveTab(0);
    }
  }

  /**
   * Close grid edit modal and reset states
   */
  closeEditModal() {
    this.isEditModalVisible = false;
    this.resetColumnEdit();
    this.resetActionsEditStates();
    this.validationErrors = [];
    this.validationAlertVisible = false;
    this.modalInitialized = false;
  }

  /**
   * Handle populated data changes from columns component
   */
  onPopulatedDataChange(populatedData: Record<string, unknown>[]) {
    this.populatedData = populatedData;
  }

  /**
   * Handle edit column request from columns component
   */
  onEditColumnRequest(event: { column: CrudTableViewColumn; index: number }) {
    if (!event || !this.tempValue?.columns?.[event.index]) {
      return;
    }

    this.columnToEdit = event;
    this.cdr.detectChanges();
  }

  /**
   * Handle column saved event from settings component
   */
  onColumnSaved(event: { column: CrudTableViewColumn; index: number }) {
    if (event && this.tempValue?.columns) {
      this.tempValue.columns[event.index] = event.column;
      this.columnToEdit = event;
      this.onColumnsChange();
    }
  }

  /**
   * Reset column edit state
   */
  resetColumnEdit() {
    this.columnToEdit = null;
    this.cdr.detectChanges();
  }

  /**
   * Add a new column to the grid
   */
  onAddColumnClick() {
    if (this.gridBuilderColumnsComponent) {
      this.gridBuilderColumnsComponent.addColumn();
    }
  }

  /**
   * Reset actions edit states in settings component
   */
  resetActionsEditStates() {
    if (this.gridBuilderSettingsComponent) {
      this.gridBuilderSettingsComponent.resetActionsEditStates();
    }
  }

  /**
   * Handle column deleted event
   */
  onColumnDeleted() {
    this.resetColumnEdit();
  }

  /**
   * Handle data change from JSON editor
   */
  onDataChange(event: any) {
    if (event) {
      this.tempValue = cloneDeep(event);
      this.jsonEditorSaved = true;
      this.checkIfErrorsResolved();
    }
  }

  /**
   * Open JSON editor modal
   */
  openJsonEditorModal() {
    this.jsonModalBackupData = cloneDeep(this.tempValue);
    this.jsonEditorSaved = false;
    this.isJsonEditorModalVisible = true;
  }

  /**
   * Close JSON editor modal
   */
  closeJsonEditorModal() {
    this.isJsonEditorModalVisible = false;

    if (!this.tempValue) {
      this.tempValue = this.jsonModalBackupData;
    }

    this.tempValueWithoutChanges = cloneDeep(this.tempValue);
    this.checkIfErrorsResolved();
  }

  /**
   * Handle JSON editor visibility change
   */
  onJsonEditorVisibleChange(visible: boolean) {
    this.isJsonEditorModalVisible = visible;

    if (!visible) {
      if (!this.jsonEditorSaved && this.jsonModalBackupData) {
        this.tempValue = cloneDeep(this.jsonModalBackupData);
      }
      this.jsonEditorSaved = false;
    }
  }

  // Private methods

  /**
   * Update validation messages based on current field values
   */
  private updateValidationMessages() {
    const missingFields: string[] = [];

    // Check columns
    if (this.tempValue?.columns) {
      this.tempValue.columns.forEach((column, index) => {
        const columnNumber = index + 1;
        if (!column.title) {
          missingFields.push(`Column ${columnNumber}: Title is required`);
        }
        if (!column.fieldName) {
          missingFields.push(`Column ${columnNumber}: Field name is required`);
        }
        if (!column.type) {
          missingFields.push(`Column ${columnNumber}: Type is required`);
        }
      });
    }

    // Check list actions
    if (this.tempValue?.listActions?.length > 0) {
      this.tempValue.listActions.forEach((action, index) => {
        const actionNumber = index + 1;
        if (!action.name) {
          missingFields.push(`List Action ${actionNumber}: Name is required`);
        }
      });
    }

    // Check item actions
    if (this.tempValue?.itemActions?.length > 0) {
      this.tempValue.itemActions.forEach((action, index) => {
        const actionNumber = index + 1;
        if (!action.name) {
          missingFields.push(`Item Action ${actionNumber}: Name is required`);
        }
      });
    }

    // Update alert only if there are still errors
    if (missingFields.length > 0) {
      this.validationAlertConfig = {
        type: AlertTypes.Error,
        title: {
          type: HeadingType.H5,
          content: 'Required Fields Missing'
        },
        description: <TextComponentConfig>{ text: missingFields.join('\n') },
        showIcon: true,
        closeable: false
      };
    } else {
      // If no errors remain, hide the alert
      this.validationAlertVisible = false;
      this.validationErrors = [];
    }
  }

  /**
   * Validate columns and required fields
   */
  private validateColumns(isSaveOperation = false): boolean {
    if (!this.tempValue?.columns || this.tempValue.columns.length === 0) {
      this.validationErrors = [{
        id: 'no-columns',
        message: 'Please add at least one column to the table configuration.'
      }];

      if (isSaveOperation) {
        this.updateValidationAlert();
        this.validationAlertVisible = true;
        this.notificationService.createNotification(
          'error',
          'Validation Error',
          'Please add at least one column to proceed.'
        );
      }
      return false;
    }

    const columnErrors: number[] = [];
    const listActionErrors: number[] = [];
    const itemActionErrors: number[] = [];
    const missingFields: string[] = [];

    // Validate column fields
    this.tempValue.columns.forEach((column, index) => {
      const columnNumber = index + 1;
      const hasErrors = !column.title || !column.fieldName || !column.type;

      if (hasErrors) {
        columnErrors.push(columnNumber);
        if (!column.title) {
          missingFields.push(`Column ${columnNumber}: Title is required`);
        }
        if (!column.fieldName) {
          missingFields.push(`Column ${columnNumber}: Field name is required`);
        }
        if (!column.type) {
          missingFields.push(`Column ${columnNumber}: Type is required`);
        }
      }
    });

    // Validate list actions
    if (this.tempValue.listActions && this.tempValue.listActions.length > 0) {
      this.tempValue.listActions.forEach((action, index) => {
        const actionNumber = index + 1;
        const hasErrors = !action.name;

        if (hasErrors) {
          listActionErrors.push(actionNumber);
          if (!action.name) {
            missingFields.push(`List Action ${actionNumber}: Name is required`);
          }
        }
      });
    }

    // Validate item actions
    if (this.tempValue.itemActions && this.tempValue.itemActions.length > 0) {
      this.tempValue.itemActions.forEach((action, index) => {
        const actionNumber = index + 1;
        const hasErrors = !action.name;

        if (hasErrors) {
          itemActionErrors.push(actionNumber);
          if (!action.name) {
            missingFields.push(`Item Action ${actionNumber}: Name is required`);
          }
        }
      });
    }

    if (columnErrors.length > 0 || listActionErrors.length > 0 || itemActionErrors.length > 0) {
      this.validationErrors = [];
      this.addValidationError('validation-errors', '');

      this.validationAlertConfig = {
        type: AlertTypes.Error,
        title: {
          type: HeadingType.H5,
          content: 'Required Fields Missing'
        },
        description: <TextComponentConfig>{ text: missingFields.join('\n') },
        showIcon: true,
        closeable: false
      };

      if (isSaveOperation) {
        this.validationAlertVisible = true;
        this.notificationService.createNotification(
          'error',
          'Validation Error',
          'Please complete all required fields to save the configuration.'
        );
      }

      return false;
    }

    return true;
  }

  /**
   * Add validation error with unique ID
   */
  private addValidationError(id: string, message: string) {
    if (!this.validationErrors.some(error => error.id === id)) {
      this.validationErrors.push({ id, message });
    }
  }

  /**
   * Check if errors are resolved with recent changes
   */
  private checkIfErrorsResolved() {
    if (!this.validationAlertVisible) {
      return;
    }

    const isValid = this.validateColumns(false);

    if (isValid) {
      this.validationAlertVisible = false;
      this.validationErrors = [];
    }
  }

  /**
   * Update validation alert based on current errors
   */
  private updateValidationAlert() {
    if (this.validationErrors.length > 0) {
      const errorMessages = this.validationErrors
        .filter(error => error.message)
        .map(error => error.message);

      this.validationAlertConfig = {
        type: AlertTypes.Error,
        title: {
          type: HeadingType.H5,
          content: 'Required Fields Missing'
        },
        description: <TextComponentConfig>{ text: errorMessages.join('\n') },
        showIcon: true,
        closeable: false
      };
      this.validationAlertVisible = true;
    } else {
      this.validationAlertVisible = false;
    }
  }
}
